<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue'
import type { ConsentCookies } from '~~/types/cookies'

const { $gtm, $posthog } = useNuxtApp()
const { isOpen, cookies, hasConsented } = useCookieConsent()
const route = useRoute()
const isStoryBlok = computed(() => !!route.query._storyblok)
const disabled = computed(() => !!route.query.disable_cookie_banner)
const isModalOpen = computed(
  () => (!hasConsented.value && !isStoryBlok.value && !disabled.value) || isOpen.value,
)
const { terms, privacyPolicy } = useLinks()

watch(() => isModalOpen.value, (isModalOpen) => {
  if (isModalOpen && !isOpen.value)
    isOpen.value = true
}, { immediate: true })

const configOpen = ref(false)

const localOpts = reactive<ConsentCookies>(
  {
    ad: true,
    analytics: true,
    personalization: true,
    functionality: true,
    security: true,
    ...(cookies.value as Partial<ConsentCookies>),
  },
)

// Make optional cookies disabled by default if
// configuration opened
watch(configOpen, (open) => {
  if (open) {
    localOpts.ad = false
    localOpts.analytics = false
    localOpts.personalization = false
  }
})

const acceptCookies = () => {
  isOpen.value = false
  cookies.value = { ...localOpts }
  $gtm.push({ event: 'update_cookie_consent' })
  if (localOpts.analytics)
    $posthog.enable()
}

const initialFocusElement = ref<ComponentPublicInstance | undefined>(undefined)
</script>

<template>
  <DTModal
    :model-value="isModalOpen"
    size="lg"
    :initial-focus="initialFocusElement?.$el"
    overlay-class="bg-black/70"
    dialog-class="!rounded-lg sm:my-8 max-w-[500px] lg:max-w-[740px] border"
    container-class="!items-start p-3"
  >
    <DTModalBody class="flex flex-col !p-6">
      <div class="flex flex-col lg:flex-row">
        <div class="flex max-w-lg flex-col space-y-2">
          <h2 class="text-lg font-bold">
            {{ $t('cookie-consent.title') }}
          </h2>
          <div class="space-y-1">
            <i18n-t
              keypath="cookie-consent.message"
              class="text-sm text-gray-600"
              tag="p"
            >
              <template #privacyPolicyLink>
                <DTButton
                  variant="link"
                  :to="privacyPolicy"
                  external
                  size="sm"
                  dense
                  target="_blank"
                >
                  {{ $t('cookie-consent.privacy') }}
                </DTButton>
              </template>
            </i18n-t>
            <ul class="list-disc pl-5 text-sm text-gray-600">
              <li>{{ $t('cookie-consent.usage.functionality') }}</li>
              <li>{{ $t('cookie-consent.usage.analytics') }}</li>
              <li>{{ $t('cookie-consent.usage.advertising') }}</li>
            </ul>
          </div>
          <i18n-t
            keypath="cookie-consent.messageAccept"
            tag="p"
            class="text-sm text-gray-600"
          >
            <template #accept>
              <strong class="font-semibold">{{ $t('cookie-consent.accept') }}</strong>
            </template>
          </i18n-t>
          <p class="text-sm text-gray-600">
            {{ $t('cookie-consent.edit-message') }}
          </p>
          <div
            class="flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-600"
          >
            <DTButton
              variant="link"
              :to="privacyPolicy"
              target="_blank"
              external
              size="sm"
              dense
            >
              {{ $t('cookie-consent.privacy') }}
            </DTButton>
            <DTButton
              variant="link"
              :to="terms"
              target="_blank"
              external
              size="sm"
              dense
            >
              {{ $t('cookie-consent.terms') }}
            </DTButton>
            <DTButton
              variant="link"
              to="https://business.safety.google/privacy/"
              target="_blank"
              external
              size="sm"
              dense
            >
              {{ $t('cookie-consent.googleDataUsage') }}
            </DTButton>
          </div>
        </div>
        <div
          class="
              mt-6
              flex
              grow
              items-center
              justify-center
              gap-2
              lg:ml-6
              lg:mt-0
              lg:justify-end
              "
        >
          <DTButton
            v-if="!configOpen"
            ref="initialFocusElement"
            class="!px-3 !py-2 !font-semibold !text-gray-800/60"
            variant="plain"
            @click="configOpen = true"
          >
            {{ $t('cookie-consent.edit') }}
          </DTButton>
          <div class="flex flex-col items-center gap-6">
            <DTButton
              variant="blue"
              class="whitespace-nowrap !rounded !px-3 !py-2 !font-semibold"
              @click="acceptCookies"
            >
              {{ $t('cookie-consent.accept') }}
            </DTButton>
          </div>
        </div>
      </div>
      <div
        v-if="configOpen"
        class="mt-6 flex flex-col gap-3"
      >
        <DTDivider />
        <div class="flex max-w-md flex-col gap-3 divide-y text-gray-800">
          <div class="pb-1">
            <DTSwitch
              v-model="localOpts.functionality"
              disabled
              :label="$t('cookie-consent.functionalityLabel')"
            />
            <p class="mb-0 pl-9 text-xs text-gray-600">
              {{ $t('cookie-consent.functionalityDesc') }}
            </p>
          </div>
          <div class="pb-1 pt-3">
            <DTSwitch
              v-model="localOpts.security"
              disabled
              :label="$t('cookie-consent.securityLabel')"
            />
            <p class="mb-0 pl-9 text-xs text-gray-600">
              {{ $t('cookie-consent.securityDesc') }}
            </p>
          </div>
          <div class="pb-1 pt-3">
            <DTSwitch
              v-model="localOpts.ad"
              :label="$t('cookie-consent.adLabel')"
            />
            <i18n-t
              keypath="cookie-consent.adDesc"
              tag="p"
              class="mb-0 pl-9 text-xs text-gray-600"
            >
              <template #privacyPolicyLink>
                <DTButton
                  variant="link"
                  :to="privacyPolicy"
                  target="_blank"
                  external
                  size="xs"
                  dense
                  class="!text-gray-600 hover:!text-gray-700"
                >
                  {{ $t('cookie-consent.privacy') }}
                </DTButton>
              </template>
            </i18n-t>
            <NuxtLink
              href="https://business.safety.google/privacy/"
              target="_blank"
              external
              class="pl-9 text-xs text-gray-600 underline hover:text-gray-700"
            >
              {{ $t('cookie-consent.adDescLink') }}
            </NuxtLink>
          </div>
          <div class="pb-1 pt-3">
            <DTSwitch
              v-model="localOpts.analytics"
              :label="$t('cookie-consent.analyticsLabel')"
            />
            <p class="mb-0 pl-9 text-xs text-gray-600">
              {{ $t('cookie-consent.analyticsDesc') }}
            </p>
          </div>
          <div class="pt-3">
            <DTSwitch
              v-model="localOpts.personalization"
              :label="$t('cookie-consent.personalisationLabel')"
            />
            <p class="mb-0 pl-9 text-xs text-gray-600">
              {{ $t('cookie-consent.personalisationDesc') }}
            </p>
          </div>
        </div>
      </div>
    </DTModalBody>
  </DTModal>
</template>
